import { Button, Modal, type ModalProps } from 'flowbite-react';
import { AlertTriangleIcon, CoinsIcon } from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { RouteIndex } from '../../../app/routeIndex';
import { setReturnPath } from '../../../lib/creditTopUpReturn.utils';
import { pathWithParams } from '../../../lib/routing.utils';

export interface InsufficientCreditsModalProps extends ModalProps {
    currentBalance: number;
    requiredCredits?: number;
    featureName?: string;
    onTopUp?: () => void;
}

/**
 * Insufficient Credits Modal
 * 
 * Displays when user attempts to use an AI feature without sufficient credits.
 * Shows current balance, required credits (if provided), and offers to navigate
 * to the top-up page.
 */
export const InsufficientCreditsModal = ({
    show,
    onClose,
    currentBalance,
    requiredCredits,
    featureName,
    onTopUp
}: InsufficientCreditsModalProps) => {
    const navigate = useNavigate();

    const handleTopUp = useCallback(() => {
        setReturnPath(window.location.pathname + window.location.search + window.location.hash);
        onTopUp?.();
        onClose?.();
        navigate(pathWithParams(RouteIndex.topUpCredit, {}));
    }, [navigate, onClose, onTopUp]);

    const handleCancel = useCallback(() => {
        onClose?.();
    }, [onClose]);

    return (
        <Modal show={show} size="md" onClose={onClose} popup>
            <Modal.Header />
            <Modal.Body>
                <div className="text-center">
                    <div className='flex flex-col items-center gap-4'>
                        <AlertTriangleIcon size={48} className='text-amber-500' />
                        
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-400">
                            Nedostatečný počet kreditů
                        </h3>

                        <div className='flex flex-col gap-3 text-sm text-gray-700 dark:text-gray-300 w-full'>
                            {featureName && (
                                <p>
                                    Pro použití funkce <strong>{featureName}</strong> nemáte dostatek kreditů.
                                </p>
                            )}
                            {!featureName && (
                                <p>
                                    Pro použití této AI funkce nemáte dostatek kreditů.
                                </p>
                            )}

                            <div className='flex flex-col gap-2 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700'>
                                <div className='flex justify-between items-center'>
                                    <span className='flex items-center gap-2 text-gray-600 dark:text-gray-400'>
                                        <CoinsIcon size={16} />
                                        <span>Váš aktuální zůstatek:</span>
                                    </span>
                                    <span className='font-bold text-lg text-gray-900 dark:text-white'>
                                        {currentBalance} kreditů
                                    </span>
                                </div>
                                
                                {requiredCredits !== undefined && (
                                    <div className='flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700'>
                                        <span className='text-gray-600 dark:text-gray-400'>
                                            Potřebné kredity:
                                        </span>
                                        <span className='font-semibold text-gray-900 dark:text-white'>
                                            {requiredCredits} kreditů
                                        </span>
                                    </div>
                                )}
                            </div>

                            <p className='text-gray-600 dark:text-gray-400'>
                                Dobijte si kredity a pokračujte v používání AI funkcí.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-center gap-3 mt-6">
                        <Button
                            color="primary"
                            onClick={handleTopUp}
                            className="flex-1 sm:flex-initial"
                        >
                            <CoinsIcon className="mr-2 h-5 w-5" />
                            Dobít kredit
                        </Button>
                        <Button
                            color="gray"
                            onClick={handleCancel}
                            className="flex-1 sm:flex-initial"
                        >
                            Zrušit
                        </Button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

